import AvailabilityItem from "@/components/availability/AvailabilityItem.tsx";
import AvailabilityPopup from "@/components/availability/AvailabilityPopup.tsx";
import AttentionAltIcon from "@/components/icons/AttentionAltIcon.tsx";
import PageHeading from "@/components/ui/PageHeading.tsx";
import routes from "@/config/routes.ts";
import { useModalAction } from "@/contexts/modal/Context.tsx";
import { useSEO } from "@/contexts/SeoContext.tsx";
import { useAvailabilities, useMe } from "@/data/user.ts";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";
import { AvailabilitySlot, AvailabilitySlotProgress } from "@/types";
import {
  DaysShort,
  excludeDate,
  generateSlots,
  generateSlotsExistingAvailabilities,
  getTime,
  validateSlots,
} from "@/utils/availability.ts";
import { useBreakpoint } from "@/utils/hooks/useBreakpoints.ts";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import { cn } from "@edusogno/lib/utils";
import {
  default as Button,
  default as LoadingButton,
} from "@edusogno/primitives/loading-button";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const times = getTime();

export default function Availability() {
  const { t } = useTranslation();
  const { me } = useMe();
  const isIelts = me?.item.user.products?.[0]?.course_info.course_name
    .toLowerCase()
    .includes("ielts");
  const navigation = useNavigate();
  const breakpoint = useBreakpoint();
  const { openModal } = useModalAction();
  const { setGlobalState } = useGlobals();
  const { setTitle, setDescription } = useSEO();
  const { availabilities, isLoading, reload } = useAvailabilities();

  const isSmall = ["xs", "sm"].includes(breakpoint);

  const [state, setState] = useState<number>();
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [blockInsert, setBlockInsert] = useState(true);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isValidSelection, setIsValidSelection] = useState(false);

  // get deadline
  const deadline = useMemo(() => {
    if (availabilities?.item.deadline) {
      return new Date(availabilities?.item.deadline);
    }

    return new Date();
  }, [availabilities?.item.deadline]);

  const isPastKickObj = deadline <= new Date();

  const handleSelected = useCallback(
    ({ id, isSelected }: { id: string; isSelected: boolean }) => {
      setSlots((r) => r?.map((l) => (l.id == id ? { ...l, isSelected } : l)));
    },
    [],
  );

  const isSlotSelected = useMemo(
    () => (id: string) => {
      const item = slots.find((r) => r.id === id);
      return item ? item.isSelected : false;
    },

    [slots],
  );

  useEffect(() => {
    if (me?.item.hasSpeaking) return;
    navigation(routes.agenda);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.item.hasSpeaking]);

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-background");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsValidSelection(validateSlots(slots));
  }, [slots, state]);

  useEffect(() => {
    setTitle(
      `${me?.item.user.first_name} ${me?.item.user.last_name} | Availability`,
    );
    setDescription("Availability Page");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    if (!availabilities?.item) return;

    const {
      noLimit,
      userData,
      isLastMonth,
      isSuspended,
      draftUserData,
      hasAvailability,
      isIntoRangeSuspended,
      isIntoRangeForInsert,
      isIntoRangeCurrentProduct,
    } = availabilities.item;

    const slot = userData?.length
      ? generateSlotsExistingAvailabilities(userData)
      : draftUserData?.length
      ? generateSlotsExistingAvailabilities(draftUserData)
      : null;

    if (slot?.length) {
      setSlots(slot);
    } else {
      setSlots(generateSlots());
    }

    const isIntoOneRange =
      (isIntoRangeCurrentProduct && !isSuspended) ||
      (isIntoRangeCurrentProduct && isIntoRangeSuspended);

    const isPastDeadline = dayjs(availabilities.item.deadline).isBefore(
      dayjs(),
    );

    if (isPastDeadline && !noLimit) {
      setState(AvailabilitySlotProgress.COMPLETE);
    } else {
      setState(AvailabilitySlotProgress.START);
    }

    setIsAvailable(hasAvailability);

    setBlockInsert(
      isSuspended ||
        (!isIntoRangeForInsert && !isIntoOneRange && !hasAvailability) ||
        (isIntoOneRange &&
          !isIntoRangeForInsert &&
          isPastDeadline &&
          !hasAvailability) ||
        isLastMonth ||
        (!noLimit &&
          isIntoRangeForInsert &&
          !isIntoRangeCurrentProduct &&
          !isIntoRangeSuspended),
    );
  }, [availabilities]);

  const handelOpenModal = () => {
    openModal(
      "CONFIRM_AVAILABILITY_SLOTS_MODAL",
      {
        slots,
        isLoading,
        availabilities: availabilities?.item,
        confirm: () => {
          setState(AvailabilitySlotProgress.COMPLETE);
          void reload();
        },
        cancel: () => setState(0),
      },
      {
        heading: ["de"].includes(me?.item?.user?.lang ?? "it")
          ? t("availability.popup.header")
          : "Your availability for this month recap",
      },
    );
  };

  return (
    <DashboardContentLayout
      loading={isLoading}
      className="overflow-y-scroll custom-scroll"
    >
      <PageHeading
        title={t("availability.text", { defaultValue: "Availability" })}
        button={
          <div className="flex justify-end items-center gap-4 mt-2">
            <div
              className={cn(
                "rounded-md px-5 py-2 border border-white",
                isPastKickObj ? "bg-[#2D224C]" : "bg-shade-five",
              )}
            >
              <p
                className={cn(
                  "text-14px font-medium font-[Inter]",
                  isPastKickObj ? "text-white" : "text-[--secondary]",
                )}
              >
                ✅{" "}
                {availabilities?.item.isLastMonth
                  ? ""
                  : `${
                      t(
                        `months.long.${
                          Number(availabilities?.item?.refMonthIdx) + 1 || 0
                        }`,
                      ) || "..."
                    } ${new Date().getFullYear()}`}
              </p>
            </div>

            {/* DEADLINE */}
            {!isAvailable && (
              <div
                className={cn(
                  "flex items-center rounded-md px-6 py-2 gap-2 border border-white",
                  isPastKickObj ? "bg-[#E31837]" : "bg-shade-five",
                )}
              >
                <p
                  className={cn(
                    "text-14px font-medium font-[Inter]",
                    isPastKickObj ? "text-white" : "text-[--secondary]",
                  )}
                >
                  {isPastKickObj ? t("Deadline") : t("Deadline")}
                  :&nbsp;
                  <b>
                    {t(`months.short.${deadline.getMonth() + 1}`)}{" "}
                    {deadline.getDate()}
                  </b>
                </p>

                {isPastKickObj && isIelts && !isAvailable && (
                  <p className="text-14px until-lg:mb-4 font-medium font-[Inter] text-[#fff]">
                    {t("availability.deadlineExceededMessage")}
                  </p>
                )}
              </div>
            )}
          </div>
        }
        subtitle={
          <>
            {blockInsert ? (
              <p className="">{t("availability.expired")}</p>
            ) : (
              <>
                {state === AvailabilitySlotProgress.START && (
                  <Trans
                    i18nKey="availability.sub_header.start"
                    components={{
                      b_anchor: <b className="text-shade-nine" />,
                    }}
                  />
                )}

                {state === AvailabilitySlotProgress.CONFIRM && (
                  <Trans
                    i18nKey="availability.sub_header.confirm"
                    components={{
                      b_anchor: <b className="text-[--green100]" />,
                    }}
                  />
                )}

                {state === AvailabilitySlotProgress.COMPLETE &&
                  t("availability.sub_header.complete")}
              </>
            )}
          </>
        }
      />

      <div className="flex flex-col justify-between h-full">
        <div className="flex flex-row w-full h-full gap-4">
          <div className="grid mt-8">
            {times?.map((i, key) => (
              <div
                key={`${i}-key-${key}`}
                className="flex items-center justify-center text-xs font-semibold text-center text-light md:text-14px"
              >
                <p>{i}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col w-full">
            <div className="grid grid-cols-7 mb-2 o gap-x-2 lg:mb-3">
              {DaysShort.map((day, key) => (
                <p
                  key={`${day}-key-${key}`}
                  className="text-center uppercase text-light"
                >
                  {t(day)}
                </p>
              ))}
            </div>

            <div
              id="slotGrid"
              className="grid grid-cols-7 w-full gap-1 xl:gap-2 h-full auto-rows-[calc(100%_/_14))]"
            >
              {slots.map((i) => (
                <AvailabilityItem
                  id={i.id}
                  key={i.id}
                  state={state}
                  value={i.value}
                  disabled={excludeDate(i.value) || blockInsert}
                  isSelected={isSlotSelected(i.id)}
                  blockInsert={blockInsert}
                  excludeDate={excludeDate(i.value) || blockInsert}
                  setIsSelected={({ id, isSelected }) =>
                    handleSelected({ id, isSelected })
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="lg:pl-[44px]  gap-2 mt-6 h-auto">
        {blockInsert ? (
          <div className="flex flex-row items-center w-full gap-4">
            <AttentionAltIcon className="w-6 h-auto fill-brand-secondary" />

            <p className="text-sm text-white font-[400]">
              {t("availability.emergency")}
            </p>
          </div>
        ) : (
          <>
            {state === AvailabilitySlotProgress.START && (
              <>
                {isValidSelection ? (
                  <div className="flex flex-row items-center w-full gap-4">
                    <i className="shrink-0">
                      <AttentionAltIcon className="w-6 h-auto fill-brand-secondary" />
                    </i>

                    <p className="text-xs text-white font-[400]">
                      {t("availability.notice_do")}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-row items-center justify-center w-full gap-4 py-1 rounded bg-shade-nine">
                    <p className="text-shade-three">
                      {t("availability.notice_done")}
                    </p>
                  </div>
                )}

                <div className="flex flex-row items-center justify-center gap-4 mt-3">
                  <Button
                    size="lg"
                    color="primary"
                    variant="contained"
                    onClick={() => setState((r) => (r ? r + 1 : 1))}
                    disabled={!isValidSelection}
                    className="mx-auto"
                  >
                    {t("btns.next")}
                  </Button>
                </div>
              </>
            )}

            {state === AvailabilitySlotProgress.CONFIRM && (
              <>
                <div className="flex flex-row items-center w-full gap-4">
                  <i className="shrink-0">
                    <AttentionAltIcon className="w-6 h-auto fill-brand-secondary" />
                  </i>

                  <p className="text-[12px] leading-[110%] text-white font-[400]">
                    {t("availability.notice_confirm")}
                  </p>
                </div>

                <div className="flex flex-row items-center justify-center gap-4 mt-3">
                  <Button
                    size="md"
                    color="white"
                    variant="contained"
                    onClick={() => setState((r) => (r ? r - 1 : 0))}
                  >
                    {t("btns.back")}
                  </Button>

                  <LoadingButton
                    size="md"
                    color="primary"
                    variant="contained"
                    loading={isLoading}
                    onClick={() =>
                      isSmall
                        ? navigation("confirm", {
                            state: {
                              slots: slots,
                              availabilities: availabilities?.item,
                            },
                          })
                        : handelOpenModal()
                    }
                  >
                    {t("btns.next")}
                  </LoadingButton>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {isSmall && !isValidSelection && !blockInsert && (
        <AvailabilityPopup isValidSelection={isValidSelection} />
      )}
    </DashboardContentLayout>
  );
}
